import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { ImagePickerProvider, useImagePickerContext } from '../ImagePickerContext'
import { ImagePickerProviderProps } from '../ImagePickerContextTypes'
import Styles from './ImagePickerDrawerV2.module.scss'
import ModalStyles from '../ImagePickerModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Background } from 'blaze-ui/icons/32'
import Camera from 'blaze-ui/icons/20/Camera'
import { ImageLink, Images, Upload5 } from 'blaze-ui/icons'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { Stock } from '../Stock/Stock'
import { AllUploadsContent } from '../Uploads/AllUploads'
import { Link } from '../Link/Link'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import cx from 'classnames'
import { ConfirmButton } from '../ConfirmButton'
import { MobileImageButton } from 'components/Design/MobileImageButtons/MobileImageButtons'
import { Item } from '@react-stately/collections'

const ImagePickerDrawerV2 = (props: DrawerProps) => {
  const imagePickerContext = useImagePickerContext()
  const { provider, setProvider, onUploadStart, uploadImages, onConfirm } = imagePickerContext

  const handleOnUploadChange = async (files: File[]) => {
    const toast = showToastMessage('Uploading...', 'loading', undefined, 0)
    await onUploadStart?.(files)
    const uploadedImages = await uploadImages(files, imagePickerContext)
    toast()
    onConfirm(uploadedImages, imagePickerContext)
  }

  return (
    <Drawer.Root {...props} handleOnly>
      <Drawer.Portal>
        <Drawer.Content>
          <Drawer.InnerContent isSnappedTop fullHeight>
            <Drawer.Top>
              <div />
              <Drawer.Title asChild>
                <Heading level={5}>Add Media</Heading>
              </Drawer.Title>
              <Drawer.Close label="Cancel" position="right" variant="ghost" />
            </Drawer.Top>
            <Drawer.Container>
              <div className={cx(Styles.selectImageContainer, ModalStyles.v2Body)}>
                <Heading level={5}>Upload your media</Heading>
                <div className={Styles.buttonsContainer}>
                  <MobileImageButton
                    icon={() => <Background size={20} />}
                    label="Files"
                    onChangeInput={(e) => {
                      const files = Array.from(e.target.files || [])
                      handleOnUploadChange(files)
                    }}
                    variant="secondary"
                    buttonClassName={Styles.button}
                  />
                  <MobileImageButton
                    icon={Camera}
                    label="Camera"
                    onChangeInput={(e) => {
                      const files = Array.from(e.target.files || [])
                      handleOnUploadChange(files)
                    }}
                    capture="environment"
                    variant="secondary"
                    buttonClassName={Styles.button}
                  />
                  <MobileImageButton
                    icon={ImageLink}
                    label="Media Link"
                    onPress={() => setProvider('link')}
                    variant="secondary"
                    buttonClassName={Styles.button}
                  />
                </div>
                <Heading level={5}>Or use media already uploaded</Heading>
                <SegmentedControl
                  variant="secondary"
                  rounded
                  isFullWidth
                  activeItem={!provider ? 'uploads' : provider}
                  onItemClick={(name) => setProvider(name === 'uploads' ? 'uploads' : 'stock')}
                >
                  <Item key="uploads" textValue="Image Library">
                    <Upload5 />
                    Image Library
                  </Item>
                  <Item key="stock" textValue="Stock Media">
                    <Images />
                    Stock Media
                  </Item>
                </SegmentedControl>
                {provider === 'stock' && <Stock containerClassName={ModalStyles.stock} />}
                {provider === 'uploads' && <AllUploadsContent containerClassName={ModalStyles.allUploads} />}
                {provider === 'link' && <Link containerClassName={ModalStyles.link} />}
              </div>
            </Drawer.Container>
          </Drawer.InnerContent>
          <div className={Styles.footer}>
            <ConfirmButton />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

interface ContainerProps extends DrawerProps, Omit<ImagePickerProviderProps, 'children'> {}

const ImagePickerDrawerV2Container = ({
  onOpen,
  onClose,
  handleClose,
  stackIndex,
  open,
  ...providerProps
}: ContainerProps) => {
  const drawerProps = { onOpen, onClose, handleClose, stackIndex, open }

  return (
    <ImagePickerProvider {...providerProps}>
      <ImagePickerDrawerV2 {...drawerProps} />
    </ImagePickerProvider>
  )
}

export { ImagePickerDrawerV2Container as ImagePickerDrawerV2 }
